// Startseite „Art wählen“ (A-01, A-02): Kacheln für Anrufe, E-Mails, SMS, WhatsApp, Meetings, Notizen mit Zahlen, darunter die
// Custom Activities mit Suche. Besser als Close: die Zahlen stehen schon da, und alle Typen stehen zur Wahl, nicht nur eine Suche.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { aktivitaetenApi, type AktivitaetenArtInfo, type CustomTypInfo } from "../api/aktivitaeten";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useSeitenKopf } from "../daten/seite";
import { T } from "../texte";
import { ARTEN, artPfad, customPfad } from "./arten";

const normal = (s: string) => s.toLocaleLowerCase("de").normalize("NFD").replace(/[̀-ͯ]/g, "");

function zahl(n: number | null | undefined): string {
  return n === null || n === undefined ? "" : n.toLocaleString("de-DE");
}

export function AktivitaetenStart() {
  useSeitenKopf([{ label: T.nav.aktivitaeten }]);
  const [arten, setArten] = useState<AktivitaetenArtInfo[] | null>(null);
  const [custom, setCustom] = useState<CustomTypInfo[]>([]);
  const [fehler, setFehler] = useState<unknown>(null);
  const [suche, setSuche] = useState("");
  const [nochmal, setNochmal] = useState(0);

  useEffect(() => {
    let gueltig = true;
    setFehler(null);
    aktivitaetenApi
      .arten()
      .then((r) => {
        if (!gueltig) return;
        setArten(r.data);
        setCustom(r.custom);
      })
      .catch((e) => gueltig && setFehler(e));
    return () => {
      gueltig = false;
    };
  }, [nochmal]);

  const zahlen = useMemo(() => new Map((arten ?? []).map((a) => [a.art, a.anzahl])), [arten]);
  const typen = useMemo(() => {
    const n = normal(suche.trim());
    return n ? custom.filter((c) => normal(c.name).includes(n)) : custom;
  }, [custom, suche]);

  return (
    <div className="akt-start">
      <div className="akt-start__karte">
        <h1 className="akt-start__titel">Aktivitäten</h1>
        <p className="akt-start__text">Alle Anrufe, Mails, Nachrichten, Notizen und Custom Activities über alle Leads, mit Filter nach Mitarbeiter und Zeitraum. Art wählen:</p>
        {fehler ? <FehlerBox fehler={fehler} onNochmal={() => setNochmal((n) => n + 1)} /> : null}
        {!arten && !fehler ? <Laden /> : null}
        {arten ? (
          <div className="akt-start__kacheln">
            {ARTEN.map((a) => (
              <Link key={a.art} to={artPfad(a.art)} className="akt-kachel">
                <Icon name={a.icon} groesse={16} />
                <span className="akt-kachel__text">
                  <span className="akt-kachel__name">{a.label}</span>
                  <span className="akt-kachel__unter">{a.untertitel}</span>
                </span>
                <span className="akt-kachel__zahl">{zahl(zahlen.get(a.art))}</span>
                <Icon name="chevron-rechts" groesse={14} className="akt-kachel__pfeil" />
              </Link>
            ))}
          </div>
        ) : null}
        {arten ? (
          <div className="akt-start__kasten">
            <h2 className="akt-start__unter">Custom Activities</h2>
            {custom.length > 6 ? (
              <label className="suchfeld akt-start__suche">
                <Icon name="suche" groesse={14} />
                <input value={suche} placeholder="Custom Activity suchen …" onChange={(e) => setSuche(e.target.value)} aria-label="Custom Activities durchsuchen" />
              </label>
            ) : null}
            {typen.length ? (
              <ul className="akt-start__typen">
                {typen.map((c) => (
                  <li key={c.id}>
                    <Link to={customPfad(c.id)} className="akt-typ">
                      <Icon name="aktivitaet" groesse={15} />
                      <span className="akt-typ__name">{c.name}</span>
                      {c.archiviert ? <span className="akt-chip">archiviert</span> : null}
                      <span className="akt-kachel__zahl">{zahl(c.anzahl)}</span>
                      <Icon name="chevron-rechts" groesse={14} className="akt-kachel__pfeil" />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="akt-start__leer">{custom.length ? "Keine passende Custom Activity." : "Es gibt noch keine Custom Activities."}</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
